import type { PontoAcessoConfiguravel } from '../../../api'
import { useEffect, useState } from 'react'
import { Alert, Box, Button, Checkbox, Dialog, FormControlLabel, Paper, Stack, Typography } from '@mui/material'
import { DialogActions, DialogContent, DialogTitle } from '@/components/dialog-componentes'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'

interface DialogReplicarConfigTotemProps {
  aberto: boolean
  onFechar: () => void
  origem: PontoAcessoConfiguravel | null
  destinos: PontoAcessoConfiguravel[]
  onConfirmar: (idsDestino: number[]) => void | Promise<void>
}

function rotuloPonto(ponto: PontoAcessoConfiguravel): string {
  return ponto.numero?.trim() || ponto.codigo
}

export function DialogReplicarConfigTotem({ aberto, onFechar, origem, destinos, onConfirmar, }: DialogReplicarConfigTotemProps): JSX.Element | null {
  const [selecionados, setSelecionados] = useState<number[]>([])

  useEffect(() => {
    if (aberto) setSelecionados(destinos.map((d) => d.id))
  }, [aberto, origem?.id])

  if (!origem) return null

  const todosSelecionados = destinos.length > 0 && selecionados.length === destinos.length
  const algunsSelecionados = selecionados.length > 0 && !todosSelecionados

  const alternar = (id: number): void => {
    setSelecionados((atuais) => atuais.includes(id) ? atuais.filter((i) => i !== id) : [...atuais, id],)
  }

  const alternarTodos = (): void => {
    setSelecionados(todosSelecionados ? [] : destinos.map((d) => d.id))
  }

  return (
    <Dialog open={aberto} onClose={onFechar} fullWidth maxWidth="sm">
      <DialogTitle onClose={onFechar}>
        Replicar a configuração de {rotuloPonto(origem)}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <Alert severity="warning">
            Ao salvar, a configuração deste totem <strong>substituirá</strong> a configuração dos totens selecionados abaixo. Essa ação não pode ser desfeita automaticamente.
          </Alert>

          <FormControlLabel
            label="Selecionar todos"
            control={
              <Checkbox
                checked={todosSelecionados}
                indeterminate={algunsSelecionados}
                onChange={alternarTodos}
                color={algunsSelecionados ? 'default' : 'primary'}
              />
            }
          />

          <Stack spacing={1}>
            {destinos.map((destino) => {
              const temConfigPropria = !!destino.configuracao && Object.keys(destino.configuracao).length > 0

              return (
                <Paper key={destino.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                    <Checkbox
                      checked={selecionados.includes(destino.id)}
                      onChange={() => alternar(destino.id)}
                    />

                    <Box>
                      <Typography variant="subtitle2" sx={{ lineHeight: 1.3 }}>
                        {rotuloPonto(destino)}
                      </Typography>

                      <Typography
                        variant="caption"
                        color={temConfigPropria ? 'warning.main' : 'text.secondary'}
                      >
                        {temConfigPropria ? 'Tem configuração própria — será substituída' : 'Sem configuração própria'}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              )
            })}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          variant="contained"
          startIcon={<ContentCopyIcon />}
          disabled={selecionados.length === 0}
          onClick={() => void onConfirmar(selecionados)}
          sx={{ textTransform: 'none' }}
        >
          Salvar e replicar para {selecionados.length}{' '}
          {selecionados.length === 1 ? 'totem' : 'totens'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
